import React, { Fragment, useEffect } from 'react';
import { Tooltip } from 'react-tippy';
import { FiCheck } from 'react-icons/fi';
import { Link } from 'gatsby';
import Button from '../../Button';
import features from '../../../constants/features';
import pricingPlans from '../../../constants/plans';

const plans = [
  pricingPlans.paperformEssentials,
  pricingPlans.paperformPro,
  pricingPlans.paperformBusiness,
  pricingPlans.paperformEnterprise,
];

const trackEnterpriseClick = (location) => {
  if (window.analytics) {
    window.analytics.track('Enterprise Click', { location });
  }
};

export default function PaperformPricing({ annual, showFeatures = true }) {
  useEffect(() => {
    if (!window.Paperform) {
      const script = document.createElement('script');
      script.src = 'https://paperform.co/__embed.min.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  return (
    <table className="lg:leading-[25px] text-[13px] leading-[18px] lg:text-[16px] min-w-[900px] table-fixed">
      <tbody>
        <tr style={{ verticalAlign: 'top' }}>
          {showFeatures ? <td className="w-[200px] lg:w-auto" /> : null}
          {plans.map((plan) => (
            <td
              key={plan.title}
              className={`font-sonoma border-r-8 border-white flex-col py-6 pb-0 px-3 text-center ${plan.tailwind}`}
            >
              <div className="mb-6 mt-3 h-[124px] flex items-center justify-center">
                {plan.icon()}
              </div>
              <div className="text-lg lg:text-2xl mb-3">{plan.title}</div>
              {plan.free || plan.enterprise ? (
                <div>
                  <Link
                    className="text-base lg:text-lg mb-1 hover:underline !text-black"
                    href="https://talk-to-sales.paperform.co"
                  >
                    Get a quote
                  </Link>

                  <div className="lg:mb-3">&#xfeff;</div>
                </div>
              ) : annual ? (
                <div>
                  <div className="text-base lg:text-lg mb-1">
                    {plan.price.annualMonthly}/month
                  </div>
                  <div className="lg:mb-3">
                    Billed as {plan.price.annual}/year
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-base lg:text-lg mb-1">
                    {plan.price.monthly}/month
                  </div>
                  <div className="lg:mb-3">Billed monthly</div>
                </div>
              )}

              <div className="font-worksans text-sm text-black/50 px-4 mt-3 mb-7 hidden lg:block">
                {plan.description}
              </div>
            </td>
          ))}
        </tr>
        {showFeatures ? (
          <tr>
            <td className="w-[200px] lg:w-auto px-3 py-1 opacity-60 italic">
              *All pricing is in USD, and exclude any sales taxes, including
              VAT.
            </td>

            {plans.map((plan) => (
              <td
                key={plan.title}
                className={`font-sonoma border-r-8 border-white flex-col align-top px-3 text-center ${plan.tailwind}`}
              >
                {plan.enterprise ? (
                  <Button
                    midnight
                    href="#chat=I want to talk to sales about Paperform Enterprise"
                    data-popup-button
                    onClick={() => {
                      trackEnterpriseClick('Pricing Table');
                    }}
                  >
                    Talk to Sales
                  </Button>
                ) : (
                  <Button midnight href={plan.link} solid>
                    Try It
                  </Button>
                )}
                {plan.extraCTA ? (
                  <div className="pt-2">{plan.extraCTA}</div>
                ) : null}
              </td>
            ))}
          </tr>
        ) : null}
        {showFeatures
          ? Object.keys(features).map((group) => (
              <Fragment key={group}>
                {group.trim() ? (
                  <tr className="font-semibold text-base">
                    <td className="px-3 py-2 pt-12">{group}</td>
                    {plans.map((plan) => (
                      <td
                        key={plan.title}
                        style={{
                          backgroundColor: `rgba(${plan.colorRGB},.8)`,
                        }}
                        className="px-3 border-r-8 border-white mr-2"
                      />
                    ))}
                  </tr>
                ) : null}

                {Object.keys(features[group]).map((feature) => {
                  // Skip annual features when showing monthly pricing
                  if (!annual && feature.startsWith('annual_')) {
                    return null;
                  }
                  // Skip monthly features when showing annual pricing (if there's an annual version available)
                  if (
                    annual &&
                    !feature.startsWith('annual_') &&
                    features[group][`annual_${feature}`] !== undefined
                  ) {
                    return null;
                  }
                  const featureObject = features[group][feature];
                  // Use annual-specific feature if available and annual is true
                  const featureKey =
                    annual && `annual_${feature}` in features[group]
                      ? `annual_${feature}`
                      : feature;

                  const featureToUse =
                    annual && `annual_${feature}` in features[group]
                      ? features[group][`annual_${feature}`]
                      : featureObject;

                  return (
                    <Tooltip
                      tag="tr"
                      key={feature}
                      animation="perspective"
                      className="group"
                      disabled={!featureToUse.help_text}
                      theme="light"
                      html={
                        <div className="text-[12px] leading-tight py-1">
                          {featureToUse.help_text}
                        </div>
                      }
                      style={{ display: 'table-row' }}
                      position="bottom"
                      trigger="mouseenter"
                      followCursor
                    >
                      <td className="px-3 py-1 bg-white group-hover:bg-stone transition-all transition-500">
                        {featureToUse.title}
                      </td>
                      {plans.map((plan) => (
                        <td
                          key={plan.title}
                          style={
                            ['boolean', 'undefined'].includes(
                              typeof plan.features[featureKey],
                            ) && !plan.features[featureKey]
                              ? {
                                  backgroundColor: `rgba(${plan.colorRGB},.8)`,
                                }
                              : null
                          }
                          className={`px-3 ${plan.tailwind} transition-all transition-500 border-r-8  border-white font-sonoma text-center`}
                        >
                          {['string', 'object', 'number'].includes(
                            typeof plan.features[featureKey],
                          )
                            ? plan.features[featureKey]
                            : null}
                          {['boolean'].includes(
                            typeof plan.features[featureKey],
                          ) ? (
                            plan.features[featureKey] ? (
                              <FiCheck size={22} />
                            ) : null
                          ) : null}
                        </td>
                      ))}
                    </Tooltip>
                  );
                })}
              </Fragment>
            ))
          : null}
        <tr style={{ verticalAlign: 'top' }}>
          {showFeatures ? <td className="w-[200px] lg:w-auto" /> : null}
          {plans.map((plan) => (
            <td
              key={plan.title}
              className={`font-sonoma border-r-8 border-white py-6 px-3 text-center ${plan.tailwind}`}
            >
              {plan.enterprise ? (
                <Button
                  midnight
                  href="#chat=I want to talk to sales about Paperform Enterprise"
                  onClick={() => {
                    trackEnterpriseClick('Pricing Table');
                  }}
                >
                  Talk to Sales
                </Button>
              ) : (
                <>
                  <Button midnight href={plan.link} solid fullwidth>
                    Try Free Now
                  </Button>
                  <div className="text-xs mt-3 opacity-50">
                    No Credit Card
                    <br />
                    Unrestricted 7 day free trial
                  </div>
                </>
              )}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}
